import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// ── Helpers localStorage ──
const LS_TOKEN   = 'crm_token';
const LS_REFRESH = 'crm_refresh';
const LS_USER    = 'crm_user';

function saveToStorage(access, refresh, user) {
  localStorage.setItem(LS_TOKEN,   access);
  localStorage.setItem(LS_REFRESH, refresh);
  localStorage.setItem(LS_USER,    JSON.stringify(user));
  window.__CRM_ACCESS_TOKEN__  = access;
  window.__CRM_REFRESH_TOKEN__ = refresh;
}

function clearStorage() {
  localStorage.removeItem(LS_TOKEN);
  localStorage.removeItem(LS_REFRESH);
  localStorage.removeItem(LS_USER);
  delete window.__CRM_ACCESS_TOKEN__;
  delete window.__CRM_REFRESH_TOKEN__;
}

// Rehydrate from localStorage on app start
const savedToken   = localStorage.getItem(LS_TOKEN);
const savedRefresh = localStorage.getItem(LS_REFRESH);
const savedUser    = (() => { try { return JSON.parse(localStorage.getItem(LS_USER)); } catch { return null; } })();
if (savedToken)   window.__CRM_ACCESS_TOKEN__  = savedToken;
if (savedRefresh) window.__CRM_REFRESH_TOKEN__ = savedRefresh;
if (savedToken)   api.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;

export const login = createAsyncThunk('auth/login', async (credentials, { rejectWithValue }) => {
  try {
    const { data } = await api.post('/auth/login/', credentials);
    saveToStorage(data.access, data.refresh, data.user);
    api.defaults.headers.common['Authorization'] = `Bearer ${data.access}`;
    return data;
  } catch (err) {
    return rejectWithValue(err.response?.data?.error || 'Erreur de connexion');
  }
});

export const logout = createAsyncThunk('auth/logout', async (_, { getState }) => {
  const { refreshToken } = getState().auth;
  try { await api.post('/auth/logout/', { refresh: refreshToken }); } catch (_) {}
  delete api.defaults.headers.common['Authorization'];
  clearStorage();
});

export const verifierAuth = createAsyncThunk('auth/verifier', async (_, { getState, rejectWithValue }) => {
  const { token, refreshToken } = getState().auth;
  if (!token || !refreshToken) return rejectWithValue('Non authentifié');
  try {
    const { data } = await api.post('/auth/token/refresh/', { refresh: refreshToken });
    localStorage.setItem(LS_TOKEN, data.access);
    window.__CRM_ACCESS_TOKEN__ = data.access;
    api.defaults.headers.common['Authorization'] = `Bearer ${data.access}`;
    return { access: data.access, refreshToken };
  } catch { return rejectWithValue('Session expirée'); }
});

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user:            savedUser,
    token:           savedToken,
    refreshToken:    savedRefresh,
    isAuthenticated: !!(savedToken && savedUser),
    loading: false,
    error:   null,
  },
  reducers: {
    clearError: (s) => { s.error = null; },
    updateUser: (s, { payload }) => {
      s.user = { ...s.user, ...payload };
      localStorage.setItem('crm_user', JSON.stringify(s.user));
    },
  },
  extraReducers: (b) => {
    b.addCase(login.pending,   (s) => { s.loading = true; s.error = null; })
     .addCase(login.fulfilled, (s, { payload }) => {
       s.loading = false; s.token = payload.access;
       s.refreshToken = payload.refresh; s.user = payload.user;
       s.isAuthenticated = true;
     })
     .addCase(login.rejected, (s, { payload }) => { s.loading = false; s.error = payload; })
     .addCase(logout.fulfilled, (s) => {
       s.user = null; s.token = null; s.refreshToken = null; s.isAuthenticated = false;
     })
     .addCase(verifierAuth.fulfilled, (s, { payload }) => { s.token = payload.access; s.isAuthenticated = true; })
     .addCase(verifierAuth.rejected,  (s) => {
       s.user = null; s.token = null; s.refreshToken = null; s.isAuthenticated = false;
       clearStorage();
     });
  },
});
export const { clearError, updateUser } = authSlice.actions;
export default authSlice.reducer;

export const demanderResetPassword = createAsyncThunk(
  'auth/demanderReset',
  async (email, { rejectWithValue }) => {
    try {
      const { data } = await api.post('/auth/reset-password/', { email });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Erreur lors de la demande');
    }
  }
);

export const confirmerResetPassword = createAsyncThunk(
  'auth/confirmerReset',
  async ({ token, password }, { rejectWithValue }) => {
    try {
      const { data } = await api.put('/auth/reset-password/', { token, password });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Token invalide ou expiré');
    }
  }
);
